import React, { ReactNode } from "react";

import { faExclamationCircle } from "@fortawesome/pro-light-svg-icons/faExclamationCircle";
import { faInfoCircle } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { notification } from "antd";
import { Col } from "antd/es/grid";
import Row from "antd/es/row";
import cn from "classnames";
import { getLocation, push, RouterLocation } from "connected-react-router";
import { chunk, groupBy, uniq } from "lodash-es";
import { all, call, put, select, takeEvery, takeLatest, takeLeading } from "redux-saga/effects";

import { EntityType } from "@mapmycustomers/shared";
import EmailEntityType from "@mapmycustomers/shared/enum/EmailEntityType";
import EmailStatus from "@mapmycustomers/shared/enum/EmailStatus";
import { CustomFieldError } from "@mapmycustomers/shared/types/customField/CustomFieldValuesUpsertResponse";
import EmailDraft from "@mapmycustomers/shared/types/draft/EmailDraft";
import Email from "@mapmycustomers/shared/types/email/Email";
import EmailAssociatedEntity from "@mapmycustomers/shared/types/email/EmailAssociatedEntity";
import EmailEntity from "@mapmycustomers/shared/types/email/EmailEntity";
import EmailPreview from "@mapmycustomers/shared/types/email/EmailPreview";
import EmailQuota from "@mapmycustomers/shared/types/email/EmailQuota";
import EmailResourceMetadata from "@mapmycustomers/shared/types/email/EmailResourceMetadata";
import EmailSyncProgress from "@mapmycustomers/shared/types/email/EmailSyncProgress";
import EmailTemplate from "@mapmycustomers/shared/types/email/EmailTemplate";
import GenerateEmailResponse from "@mapmycustomers/shared/types/email/GenerateEmailResponse";
import {
  Company,
  EntitiesSupportedByEmailFeature,
  EntityTypesSupportedByEmailFeature,
  MapEntity,
  type Person,
} from "@mapmycustomers/shared/types/entity";
import Resource from "@mapmycustomers/shared/types/entity/Resource";
import { RawFile } from "@mapmycustomers/shared/types/File";
import Organization from "@mapmycustomers/shared/types/Organization";
import ListResponse from "@mapmycustomers/shared/types/viewModel/ListResponse";
import PlatformFilterModel from "@mapmycustomers/shared/types/viewModel/platformModel/PlatformFilterModel";
import { isDefined } from "@mapmycustomers/shared/util/assert";

import AiFeatureType from "@app/enum/AiFeatureType";
import { fetchOutboxEmails, fetchOutboxStatusCounts } from "@app/scene/emailOutbox/store/actions";
import { hideMapEntityView } from "@app/scene/map/store/mapMode/actions";
import { connectToMailNylas } from "@app/scene/settings/util/connectToNylas";
import { getEmailDrafts, getEmailTemplateInfo } from "@app/store/email/selectors";
import { handleError } from "@app/store/errors/actions";
import { getOrganizationId, isEmailServiceSupported } from "@app/store/iam";
import { notifyAboutChanges } from "@app/store/uiSync/actions";
import EmailTemplateInfo from "@app/types/email/EmailTemplateInfo";
import areEmailsEqual from "@app/util/email/areEmailsEqual";
import { EMAIL_WARNING_MODAL_CLASS_NAME } from "@app/util/email/const";
import createEmailAssociatedEntity from "@app/util/email/createEmailAssociatedEntity";
import getEmailMetadataResourcesIds from "@app/util/email/getEmailMetadataResourcesIds";
import getValidationErrors from "@app/util/errorHandling/getValidationErrors";
import { formatRawDate } from "@app/util/formatters";

import styles from "../../component/view/SelectBar/components/EmailButton/EmailButton.module.scss";
import i18nService from "../../config/I18nService";
import Path from "../../enum/Path";
import SettingPath from "../../enum/settings/SettingPath";
import layout from "../../styles/layout";
import { downloadFileByUrl } from "../../util/file";
import { callApi } from "../api/callApi";
import { isEmailNylasConnected, isNylasEmailConnectionFailed } from "../nylas";

import {
  checkEmailSupporting,
  createEmailDraft,
  createEmailTemplate,
  deleteEmailAttachedFile,
  deleteEmailDraft,
  deleteEmailTemplate,
  downloadEmailAttachedFile,
  fetchEmailAttachment,
  fetchEmailDrafts,
  fetchEmailPreview,
  fetchEmailQuota,
  fetchEmailSyncProgress,
  fetchEmailTemplates,
  fetchNewEmailAssociatedEntities,
  fetchNewEmailEntities,
  fetchNewEmailPreview,
  fetchSentEmailEntities,
  generateEmailContent,
  makeEmailResourcePublic,
  sendEmail,
  shareEmailTemplate,
  submitEmailAiFeedback,
  subscribeEntitiesEmails,
  unsubscribeEntitiesEmails,
  updateEmailDraft,
  updateEmailTemplate,
  uploadFileForNewEmail,
  uploadImageForNewEmail,
} from "./actions";
import messages from "./messages";

function createEmailResourceMetadata(resource: Resource): EmailResourceMetadata {
  return {
    id: resource.id,
    fileContentLength: resource.file!.contentLength,
    fileContentType: resource.file!.contentType,
    name: resource.name,
  };
}

function createEmailDraftMetadata({
  entities,
  entityType,
  resources,
}: {
  entities: EntitiesSupportedByEmailFeature[];
  entityType: EntityTypesSupportedByEmailFeature;
  resources: Resource[];
}): EmailDraft["metadata"] {
  return {
    recipients: {
      to: entities.map((entity) => ({
        id: entity.id,
        email: entity.email,
        name: entity.name,
        type: entity.entity,
      })),
    },
    recipientType: entityType,
    resources: resources.map(createEmailResourceMetadata),
  };
}

function* fetchEmailResources(resourceIds: Resource["id"][]) {
  if (!resourceIds.length) {
    return [];
  }

  const uniqResourceIds = uniq(resourceIds);

  const orgId: Organization["id"] = yield select(getOrganizationId);

  const publicOnlyResponse: ListResponse<Resource> = yield callApi("fetchResources", orgId, {
    $filters: { $and: [{ id: { $in: uniqResourceIds } }], includePublicOnly: true },
    $limit: resourceIds.length,
  });

  const resources = [...publicOnlyResponse.data];

  if (resources.length < uniqResourceIds.length) {
    const missedResourceIds = uniqResourceIds.filter(
      (resourceId) => !resources.some((resource) => resource.id === resourceId)
    );

    const response: ListResponse<Resource> = yield callApi("fetchResources", orgId, {
      $filters: { $and: [{ id: { $in: missedResourceIds } }] },
      $limit: missedResourceIds.length,
    });

    resources.push(...response.data);
  }

  return resources;
}

function* onFetchEmailQuota({ payload }: ReturnType<typeof fetchEmailQuota.request>) {
  try {
    const orgId: Organization["id"] | undefined = yield select(getOrganizationId);
    if (orgId) {
      const emailQuota: EmailQuota = yield callApi("getEmailQuota", orgId);
      if (payload?.callback) {
        yield call(payload.callback, emailQuota);
      }
      yield put(fetchEmailQuota.success(emailQuota));
    }
  } catch (error) {
    yield put(handleError({ error }));
    yield put(fetchEmailQuota.failure());
  }
}

function* onFetchNewEmailAssociatedEntities({
  payload: { callback, deals, failureCallback, recipients, recipientType },
}: ReturnType<typeof fetchNewEmailAssociatedEntities>) {
  const orgId: Organization["id"] = yield select(getOrganizationId);

  const findDealByRecipient = (recipient: EntitiesSupportedByEmailFeature) =>
    deals.find((deal) => {
      switch (recipient.entity) {
        case EntityType.COMPANY:
          return deal.account?.id === recipient.id;
        case EntityType.PERSON:
          return deal.contact?.id === recipient.id;
      }
    });

  try {
    let emailAssociatedEntities: EmailAssociatedEntity[];

    if (!recipients.length) {
      emailAssociatedEntities = [];
    } else if (recipients.length === 1) {
      const recipient = recipients[0];
      const matchedDeal = findDealByRecipient(recipient);
      emailAssociatedEntities = [
        createEmailAssociatedEntity({
          accountId:
            matchedDeal && recipientType === EntityType.PERSON
              ? (matchedDeal.account?.id ?? null)
              : undefined,
          contactId:
            matchedDeal && recipientType === EntityType.COMPANY
              ? (matchedDeal.contact?.id ?? null)
              : undefined,
          dealId: matchedDeal?.id,
          entityId: recipient.id,
        }),
      ];
    } else {
      emailAssociatedEntities = recipients.map((recipient) =>
        createEmailAssociatedEntity({
          dealId: findDealByRecipient(recipient)?.id,
          entityId: recipient.id,
        })
      );

      if (deals.length) {
        for (const emailAssociatedEntity of emailAssociatedEntities) {
          const deal = deals.find((deal) => deal.id === emailAssociatedEntity.dealId);
          if (deal) {
            switch (recipientType) {
              case EntityType.COMPANY:
                emailAssociatedEntity.contactId = deal.contact?.id ?? null;
                break;
              case EntityType.PERSON:
                emailAssociatedEntity.accountId = deal.account?.id ?? null;
                break;
            }
          }
        }
      } else if (recipientType === EntityType.PERSON) {
        const personIds = recipients.map((recipient) => recipient.id);
        const persons: ListResponse<Person> = yield callApi("fetchPeople", orgId, {
          $filters: {
            $and: [
              {
                id: { $in: personIds },
              },
            ],
            includeAccessStatus: true,
          },
        });
        for (const emailAssociatedEntity of emailAssociatedEntities) {
          const person = persons.data.find(
            (person) => person.id === emailAssociatedEntity.entityId
          );
          if (person?.accounts?.length === 1) {
            emailAssociatedEntity.accountId = person.accounts[0].id;
          }
        }
      }
    }
    if (callback) {
      yield call(callback, emailAssociatedEntities);
    }
  } catch (error) {
    if (failureCallback) {
      yield call(failureCallback);
    }
    yield put(handleError({ error }));
  }
}

function* onFetchNewEmailPreview({
  payload: { callback, email, failureCallback },
}: ReturnType<typeof fetchNewEmailPreview>) {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    const preview: EmailPreview = yield callApi("previewEmail", orgId, email);
    if (callback) {
      yield call(callback, preview);
    }
  } catch (error) {
    if (failureCallback) {
      yield call(failureCallback);
    }
    yield put(handleError({ error }));
  }
}

function* onFetchNewEmailEntities({
  payload: { callback, entityIds, entityType, failureCallback },
}: ReturnType<typeof fetchNewEmailEntities>) {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    const response: ListResponse<EntitiesSupportedByEmailFeature> = yield callApi(
      entityType === EntityType.COMPANY ? "fetchCompanies" : "fetchPeople",
      orgId,
      {
        $filters: {
          $and: [
            {
              id: { $in: entityIds },
            },
          ],
        },
      }
    );
    yield call(callback, response.data);
  } catch (error) {
    if (failureCallback) {
      yield call(failureCallback);
    }
    yield put(handleError({ error }));
  }
}

function* onFetchEmailPreview({
  payload: { callback, email, failureCallback },
}: ReturnType<typeof fetchEmailPreview>) {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    const preview: EmailPreview = yield callApi("previewEmail", orgId, email);
    yield call(callback, preview);
  } catch (error) {
    yield call(failureCallback);
    yield put(handleError({ error }));
  }
}

export function* onUploadImageForNewEmail({
  payload: { callback, file },
}: ReturnType<typeof uploadImageForNewEmail>) {
  try {
    const FILE_SIZE_LIMIT = 25 * 1024 * 1024;

    if (file.size > FILE_SIZE_LIMIT) {
      const intl = i18nService.getIntl();
      notification.error({
        message: (
          <div>
            <div className={styles.errorTitle}>
              {intl?.formatMessage(messages.uploadFileSizeExceededError)}
            </div>
            <div className={styles.errorDescription}>
              {intl?.formatMessage(messages.uploadFileSizeExceededErrorDescription)}
            </div>
          </div>
        ),
      });
    } else {
      const orgId: Organization["id"] = yield select(getOrganizationId);
      const rawFile: RawFile = yield callApi(
        "createFile",
        orgId,
        file,
        true,
        undefined,
        undefined,
        {
          headers: {
            "x-mmc-email-attachment": true,
          },
        }
      );

      if (rawFile.publicURI) {
        yield call(callback, rawFile.publicURI);
      }
    }
  } catch (error) {
    yield put(handleError({ error }));
  }
}

function* onFetchEmailTemplates() {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);

    const response: ListResponse<EmailTemplate> = yield callApi("fetchEmailTemplates", orgId, {
      $filters: { includeAttachments: true },
    });

    const resources: Resource[] = yield fetchEmailResources(
      getEmailMetadataResourcesIds(response.data)
    );

    yield put(fetchEmailTemplates.success({ resources, templates: response.data }));
  } catch (error) {
    yield put(handleError({ error }));
    yield put(fetchEmailTemplates.failure());
  }
}

function* onCreateEmailTemplate({
  payload: { attachments, callback, resources, template },
}: ReturnType<typeof createEmailTemplate.request>) {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);

    const createdTemplate: EmailTemplate = yield callApi("createEmailTemplate", orgId, {
      attachmentIds: attachments.map(({ id }) => id),
      body: template.body,
      entityType: template.entityType,
      metadata: { resources: resources.map(createEmailResourceMetadata) },
      name: template.name,
      resourceIds: resources.map(({ id }) => id),
      subject: template.subject,
    });

    createdTemplate.attachments = attachments;

    yield put(
      createEmailTemplate.success({
        resources,
        template: createdTemplate,
      })
    );

    if (callback) {
      yield call(callback, createdTemplate);
    }
  } catch (error) {
    yield put(handleError({ error }));
    yield put(createEmailTemplate.failure());
  }
}

function* onUpdateEmailTemplate({
  payload: { attachments, callback, finishCallback, resources, template },
}: ReturnType<typeof updateEmailTemplate.request>) {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    const originalTemplateInfo: EmailTemplateInfo = yield select(getEmailTemplateInfo, template.id);

    const updatedTemplate: EmailTemplate = yield callApi("updateEmailTemplate", orgId, {
      id: template.id,
      attachmentIds:
        attachments?.map(({ id }) => id) ?? originalTemplateInfo.template.attachmentIds,
      body: template.body,
      entityType: originalTemplateInfo.template.entityType,
      metadata: resources
        ? { resources: resources.map(createEmailResourceMetadata) }
        : originalTemplateInfo.template.metadata,
      name: template.name,
      resourceIds: resources?.map(({ id }) => id) ?? originalTemplateInfo.template.resourceIds,
      subject: template.subject,
    });

    updatedTemplate.attachments = attachments ?? originalTemplateInfo.template.attachments;

    yield put(
      updateEmailTemplate.success({
        resources,
        template: updatedTemplate,
      })
    );

    if (callback) {
      yield call(callback, updatedTemplate);
    }
  } catch (error) {
    yield put(handleError({ error }));
    yield put(updateEmailTemplate.failure());
  } finally {
    if (finishCallback) {
      yield call(finishCallback);
    }
  }
}

function* onDeleteEmailTemplate({
  payload: { callback, templateId },
}: ReturnType<typeof deleteEmailTemplate.request>) {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);

    yield callApi("deleteEmailTemplate", orgId, templateId);

    yield put(deleteEmailTemplate.success(templateId));

    if (callback) {
      yield call(callback);
    }
  } catch (error) {
    yield put(handleError({ error }));
    yield put(deleteEmailTemplate.failure());
  }
}

function* onShareEmailTemplate({
  payload: { templateId, userIds },
}: ReturnType<typeof shareEmailTemplate.request>) {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    yield callApi("shareEmailTemplate", orgId, templateId, userIds);
    yield put(shareEmailTemplate.success({ templateId, userIds }));
  } catch (error) {
    yield put(handleError({ error }));
    yield put(shareEmailTemplate.failure());
  }
}

export function* onDownloadAttachedFile({
  payload: { attachedFile, preview },
}: ReturnType<typeof downloadEmailAttachedFile>) {
  try {
    if (attachedFile.file) {
      const orgId: Organization["id"] = yield select(getOrganizationId);
      const fileBlob: Blob = yield callApi(
        "fetchFile",
        orgId,
        attachedFile.file.id,
        true,
        false,
        undefined,
        undefined,
        {
          responseType: "blob",
        }
      );
      const fileUrl = window.URL.createObjectURL(fileBlob);
      if (preview) {
        window.open(fileUrl, "_blank");
      } else {
        yield call(downloadFileByUrl, fileUrl, attachedFile.file.name);
      }
    }
  } catch (error) {
    yield put(handleError({ error }));
  }
}

export function* onDeleteEmailAttachedFile({
  payload: fileId,
}: ReturnType<typeof deleteEmailAttachedFile>) {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    yield callApi("deleteFile", orgId, fileId);
  } catch (error) {
    yield put(handleError({ displayNotification: false, error }));
  }
}

export function* onFetchEmailAttachment({
  payload: { attachmentId, callback, failureCallback, messageId },
}: ReturnType<typeof fetchEmailAttachment>) {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    const file: Blob = yield callApi("fetchEmailAttachment", orgId, attachmentId, messageId, {
      responseType: "blob",
    });

    yield call(callback, file);
  } catch (error) {
    yield call(failureCallback);
    yield put(handleError({ error }));
  }
}

export function* onCheckEmailSupporting({
  payload: { cancelCallback, modal, successfulCallback },
}: ReturnType<typeof checkEmailSupporting>) {
  try {
    let success = true;
    const emailServiceSupported: boolean = yield select(isEmailServiceSupported);

    const nylasConnected: boolean = yield select(isEmailNylasConnected);
    const nylasSyncFailed: boolean = yield select(isNylasEmailConnectionFailed);

    const intl = i18nService.getIntl();
    if (!emailServiceSupported) {
      // we need to hide entity pane if this redirection was called from the map
      yield put(hideMapEntityView());
      yield put(push(`${Path.SETTINGS}/${SettingPath.PERSONAL_EMAIL_PREFERENCES}`));
      success = false;
    } else if (!nylasConnected || nylasSyncFailed) {
      modal.show({
        cancelText: intl?.formatMessage(messages.syncModalCancel),
        centered: true,
        className: cn(styles.confirmationModal, EMAIL_WARNING_MODAL_CLASS_NAME),
        closable: true,
        content: (
          <div className={styles.syncModalContent}>
            {nylasSyncFailed
              ? intl?.formatMessage(messages.syncModalFailedConnectionText)
              : intl?.formatMessage(messages.syncModalNoConnectionText, {
                  b: (text: ReactNode) => <b>{text}</b>,
                  br: <br />,
                  icon: <FontAwesomeIcon icon={faInfoCircle} />,
                })}
          </div>
        ),
        icon: null,
        okCancel: true,
        okText: nylasSyncFailed
          ? intl?.formatMessage(messages.syncModalFailedConnectionOk)
          : intl?.formatMessage(messages.syncModalNoConnectionOk),
        onCancel: cancelCallback,
        onOk: connectToMailNylas,
        title: (
          <Row align="middle" className={styles.syncModalTitle} gutter={layout.spacerM}>
            <Col>
              <FontAwesomeIcon
                className={nylasSyncFailed ? styles.warningIcon : styles.infoIcon}
                icon={faExclamationCircle}
              />
            </Col>
            <Col>
              {nylasSyncFailed
                ? intl?.formatMessage(messages.syncModalFailedConnectionTitle)
                : intl?.formatMessage(messages.syncModalNoConnectionTitle)}
            </Col>
          </Row>
        ),
        type: "info",
      });
      success = false;
    }

    if (success) {
      yield call(successfulCallback);
    }
  } catch (error) {
    yield put(handleError({ error }));
  }
}

export function* onUploadFileForNewEmail({
  payload: { attachedFile, callback, failureCallback },
}: ReturnType<typeof uploadFileForNewEmail>) {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    const file: RawFile = yield callApi(
      "createFile",
      orgId,
      attachedFile.localFile!,
      false,
      undefined,
      undefined,
      {
        headers: {
          "x-mmc-email-attachment": true,
        },
      }
    );
    if (callback) {
      yield call(callback, { ...attachedFile, file });
    }
  } catch (error) {
    if (failureCallback) {
      yield call(failureCallback);
    }
    yield put(handleError({ error }));
  }
}

function* onSendEmail({
  payload: { callback, email, failureCallback },
}: ReturnType<typeof sendEmail>) {
  const intl = i18nService.getIntl();

  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    const createdEmail: Email = yield callApi("sendEmail", orgId, email);

    if (createdEmail.status === EmailStatus.ERRORED) {
      if (failureCallback) {
        yield call(failureCallback, []);
      }
      if (intl) {
        notification.error({
          message: intl.formatMessage(messages.sendUnknownError),
        });
      }
      if (failureCallback) {
        yield call(failureCallback, []);
      }
    } else {
      if (callback) {
        yield call(callback, createdEmail);
      }
      if (intl && email.schedule) {
        notification.success({
          description: intl.formatMessage(messages.sendScheduledDescription, {
            date: formatRawDate(email.schedule.sendAt, "PPp"),
          }),
          message: intl.formatMessage(messages.sendScheduledSuccess),
        });
      }

      yield put(fetchEmailQuota.request());

      const { pathname }: RouterLocation<unknown> = yield select(getLocation);
      if (pathname.startsWith(Path.EMAIL_OUTBOX)) {
        yield put(fetchOutboxEmails.request());
        yield put(fetchOutboxStatusCounts.request());
      }
    }
  } catch (error) {
    if (failureCallback) {
      const customFieldErrors = (getValidationErrors(error) as unknown[]).filter(
        (error): error is CustomFieldError => {
          const customFieldError = error as Partial<CustomFieldError>;
          return isDefined(customFieldError.customField);
        }
      );

      yield call(failureCallback, customFieldErrors);

      if (customFieldErrors.length) {
        return;
      }
    }

    yield put(
      handleError({
        defaultMessage: messages.sendUnknownError,
        error,
      })
    );
  }
}

function* onFetchEmailSyncProgress() {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);

    const emailSyncProgress: EmailSyncProgress = yield callApi("fetchEmailSyncProgress", orgId);

    yield put(fetchEmailSyncProgress.success(emailSyncProgress));
  } catch (error) {
    yield put(handleError({ error }));
    yield put(fetchEmailSyncProgress.failure());
  }
}

function* onMakeEmailResourcePublic({
  payload: { callback, failureCallback, resourceId },
}: ReturnType<typeof makeEmailResourcePublic>) {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    const updatedResource: Resource = yield callApi("updateResource", orgId, {
      id: resourceId,
      isPublic: true,
    });
    yield put(notifyAboutChanges({ entityType: EntityType.RESOURCE, updated: [updatedResource] }));
    yield call(callback, updatedResource);
  } catch (error) {
    if (failureCallback) {
      yield call(failureCallback);
    }
    yield put(handleError({ error }));
  }
}

export function* onFetchSentEmailEntities({
  payload: { callback, emailId, failureCallback },
}: ReturnType<typeof fetchSentEmailEntities>) {
  try {
    const organizationId: Organization["id"] = yield select(getOrganizationId);

    const emailEntitiesResponse: ListResponse<EmailEntity> = yield callApi(
      "fetchEmailsEntities",
      organizationId,
      emailId
    );

    const companyIds = emailEntitiesResponse.data
      .filter((emailEntity) => emailEntity.type === EmailEntityType.COMPANY)
      .map((emailEntity) => emailEntity.id);

    const personIds = emailEntitiesResponse.data
      .filter((emailEntity) => emailEntity.type === EmailEntityType.PERSON)
      .map((emailEntity) => emailEntity.id);

    // Resolve recipients through the POST /pins endpoint so the (potentially
    // thousands of) ids travel in the request body rather than the URL query
    // string, which would otherwise exceed the server's request-line limit and
    // fail with a 414.
    const entities: NonNullable<PlatformFilterModel["entities"]> = {};
    if (companyIds.length > 0) {
      entities[EntityType.COMPANY] = { $and: [{ id: { $in: companyIds } }] };
    }
    if (personIds.length > 0) {
      entities[EntityType.PERSON] = { $and: [{ id: { $in: personIds } }] };
    }

    let emailEntities: EntitiesSupportedByEmailFeature[] = [];
    if (companyIds.length > 0 || personIds.length > 0) {
      const pinsResponse: ListResponse<MapEntity> = yield callApi("fetchPins", organizationId, {
        $filters: { entities, includeAccessStatus: true },
        $limit: companyIds.length + personIds.length,
      });
      // Only accounts and contacts were requested, so the response holds just
      // those entity types.
      emailEntities = pinsResponse.data as EntitiesSupportedByEmailFeature[];
    }

    const inaccessibleEmailEntities = emailEntitiesResponse.data.filter(
      (emailEntity) =>
        !emailEntities.some((fetchedEmailEntity) =>
          areEmailsEqual(emailEntity.email, fetchedEmailEntity.email)
        )
    );

    yield call(callback, { emailEntities, inaccessibleEmailEntities });
  } catch (error) {
    if (failureCallback) {
      yield call(failureCallback);
    }
    yield put(handleError({ error }));
  }
}

function* onFetchEmailDrafts() {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);

    const fetchDraftResponse: ListResponse<EmailDraft> = yield callApi("fetchDrafts", orgId, {
      $filters: {
        includeAttachments: true,
      },
      $limit: 100,
      $order: "-updatedAt",
    });

    const companyIds = uniq(
      fetchDraftResponse.data
        .filter((draft) => draft.metadata.recipientType === EntityType.COMPANY)
        .flatMap((draft) => draft.metadata.recipients.to.map((recipient) => recipient.id))
    );

    const personIds = uniq(
      fetchDraftResponse.data
        .filter((draft) => draft.metadata.recipientType === EntityType.PERSON)
        .flatMap((draft) => draft.metadata.recipients.to.map((recipient) => recipient.id))
    );

    const [fetchCompaniesResponse, fetchPeopleResponse, resources]: [
      ListResponse<Company>,
      ListResponse<Person>,
      Resource[],
    ] = yield all([
      companyIds.length
        ? callApi("fetchCompanies", orgId, {
            $filters: {
              $and: [{ id: { $in: companyIds } }],
            },
            $limit: companyIds.length,
          })
        : { data: [] },
      personIds.length
        ? callApi("fetchPeople", orgId, {
            $filters: {
              $and: [{ id: { $in: personIds } }],
            },
            $limit: personIds.length,
          })
        : { data: [] },
      fetchEmailResources(getEmailMetadataResourcesIds(fetchDraftResponse.data)),
    ]);

    yield put(
      fetchEmailDrafts.success({
        drafts: fetchDraftResponse.data,
        entities: [...fetchCompaniesResponse.data, ...fetchPeopleResponse.data],
        resources,
      })
    );
  } catch (error) {
    yield put(handleError({ error }));
    yield put(fetchEmailDrafts.failure());
  }
}

function* onCreateEmailDraft({
  payload: {
    attachments,
    callback,
    displayErrorNotification = false,
    draft,
    entities,
    entityType,
    failureCallback,
    finishCallback,
    resources,
  },
}: ReturnType<typeof createEmailDraft.request>) {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);

    const createdDraft: EmailDraft = yield callApi("createDraft", orgId, {
      attachmentIds: attachments.map(({ id }) => id),
      content: draft.content,
      metadata: createEmailDraftMetadata({ entities, entityType, resources }),
      title: draft.title,
      type: draft.type,
    });

    createdDraft.attachments = attachments;

    yield put(
      createEmailDraft.success({
        draft: createdDraft,
        entities,
        resources,
      })
    );

    if (callback) {
      yield call(callback, createdDraft);
    }
  } catch (error) {
    yield put(handleError({ displayNotification: displayErrorNotification, error }));
    yield put(createEmailDraft.failure());
    if (failureCallback) {
      yield call(failureCallback);
    }
  } finally {
    if (finishCallback) {
      yield call(finishCallback);
    }
  }
}

function* onUpdateEmailDraft({
  payload: { attachments, callback, draft, entities, failureCallback, finishCallback, resources },
}: ReturnType<typeof updateEmailDraft.request>) {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    const drafts: EmailDraft[] = yield select(getEmailDrafts);
    const originalDraft = drafts.find(({ id }) => id === draft.id)!;

    const updatedDraft: EmailDraft = yield callApi("updateDraft", orgId, {
      id: draft.id,
      attachmentIds: attachments.map(({ id }) => id),
      content: draft.content,
      metadata: createEmailDraftMetadata({
        entities,
        entityType: originalDraft.metadata.recipientType,
        resources,
      }),
      title: draft.title,
      type: draft.type,
    });

    updatedDraft.attachments = attachments;

    yield put(
      updateEmailDraft.success({
        draft: updatedDraft,
        entities,
        resources,
      })
    );

    if (callback) {
      yield call(callback, updatedDraft);
    }
  } catch (error) {
    yield put(handleError({ displayNotification: false, error }));
    yield put(updateEmailDraft.failure());
    if (failureCallback) {
      yield call(failureCallback);
    }
  } finally {
    if (finishCallback) {
      yield call(finishCallback);
    }
  }
}

function* onDeleteEmailDraft({
  payload: { callback, draft, failureCallback, finishCallback },
}: ReturnType<typeof deleteEmailDraft.request>) {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    yield callApi("deleteDraft", orgId, draft.id);
    yield put(deleteEmailDraft.success());
    if (callback) {
      yield call(callback);
    }
  } catch (error) {
    yield put(handleError({ displayNotification: false, error }));
    yield put(deleteEmailDraft.failure(draft));
    if (failureCallback) {
      yield call(failureCallback);
    }
  } finally {
    if (finishCallback) {
      yield call(finishCallback);
    }
  }
}

export function* onSubscribeEntitiesEmails({
  payload: { callback, entities, failureCallback, finishCallback },
}: ReturnType<typeof subscribeEntitiesEmails>) {
  const intl = i18nService.getIntl();
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);

    yield all(
      Object.entries(groupBy(entities, (entity) => entity.entity)).flatMap(
        ([entityType, groupedEntities]) =>
          chunk(groupedEntities, 100).map((groupedEntities) =>
            callApi(
              "subscribeEntitiesEmails",
              orgId,
              entityType as EntityTypesSupportedByEmailFeature,
              groupedEntities.map((entity) => entity.id)
            )
          )
      )
    );

    if (intl) {
      notification.success({ message: intl.formatMessage(messages.entitiesSubscribeSuccess) });
    }

    if (callback) {
      yield call(callback);
    }
  } catch (error) {
    if (intl) {
      notification.success({ message: intl.formatMessage(messages.entitiesSubscribeError) });
    }

    if (failureCallback) {
      yield call(failureCallback);
    }

    yield put(handleError({ displayNotification: false, error }));
  }
  if (finishCallback) {
    yield call(finishCallback);
  }
}

export function* onUnsubscribeEntitiesEmails({
  payload: { callback, entities, failureCallback, finishCallback },
}: ReturnType<typeof unsubscribeEntitiesEmails>) {
  const intl = i18nService.getIntl();
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);

    yield all(
      Object.entries(groupBy(entities, (entity) => entity.entity)).flatMap(
        ([entityType, groupedEntities]) =>
          chunk(groupedEntities, 100).map((groupedEntities) =>
            callApi(
              "unsubscribeEntitiesEmails",
              orgId,
              entityType as EntityTypesSupportedByEmailFeature,
              groupedEntities.map((entity) => entity.id)
            )
          )
      )
    );

    if (intl) {
      notification.success({ message: intl.formatMessage(messages.entitiesUnsubscribeSuccess) });
    }

    if (callback) {
      yield call(callback);
    }
  } catch (error) {
    if (intl) {
      notification.success({ message: intl.formatMessage(messages.entitiesUnsubscribeError) });
    }

    if (failureCallback) {
      yield call(failureCallback);
    }

    yield put(handleError({ error }));
  }
  if (finishCallback) {
    yield call(finishCallback);
  }
}

function* onGenerateEmailContent({
  payload: {
    callback,
    email,
    entityId,
    entityType,
    failureCallback,
    prompt,
    regenerate,
    signal,
    subjectOnly,
    tone,
  },
}: ReturnType<typeof generateEmailContent.request>) {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);

    const response: GenerateEmailResponse = yield callApi(
      "generateEmail",
      orgId,
      { email, entityId, entityType, prompt, subjectOnly, suggestNewAlternative: regenerate, tone },
      { signal }
    );

    if (!response) {
      yield put(generateEmailContent.failure());
      return;
    }

    yield call(callback, response);
    yield put(generateEmailContent.success());
  } catch (error) {
    yield call(failureCallback);
    yield put(handleError({ error }));
    yield put(generateEmailContent.failure());
  }
}

function* onSubmitEmailAiFeedback({
  payload: { comment, requestId },
}: ReturnType<typeof submitEmailAiFeedback>) {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    yield callApi("submitAiFeedback", orgId, {
      comment,
      featureType: AiFeatureType.EMAIL_GENERATE,
      requestId,
    });
    const intl = i18nService.getIntl();
    if (intl) {
      notification.success({
        description: intl.formatMessage(messages.aiFeedbackReportSentDescription),
        message: intl.formatMessage(messages.aiFeedbackReportSentTitle),
      });
    }
  } catch (error) {
    yield put(handleError({ error }));
  }
}

export function* emailSaga() {
  yield takeLeading(fetchEmailTemplates.request, onFetchEmailTemplates);
  yield takeLeading(createEmailTemplate.request, onCreateEmailTemplate);
  yield takeLeading(updateEmailTemplate.request, onUpdateEmailTemplate);
  yield takeLeading(deleteEmailTemplate.request, onDeleteEmailTemplate);
  yield takeLeading(shareEmailTemplate.request, onShareEmailTemplate);
  yield takeLeading(fetchEmailQuota.request, onFetchEmailQuota);
  yield takeLeading(uploadImageForNewEmail, onUploadImageForNewEmail);
  yield takeLeading(sendEmail, onSendEmail);
  yield takeEvery(uploadFileForNewEmail, onUploadFileForNewEmail);
  yield takeLatest(fetchNewEmailAssociatedEntities, onFetchNewEmailAssociatedEntities);
  yield takeLatest(fetchNewEmailPreview, onFetchNewEmailPreview);
  yield takeLatest(fetchNewEmailEntities, onFetchNewEmailEntities);
  yield takeLatest(fetchEmailPreview, onFetchEmailPreview);
  yield takeLeading(downloadEmailAttachedFile, onDownloadAttachedFile);
  yield takeLeading(deleteEmailAttachedFile, onDeleteEmailAttachedFile);
  yield takeEvery(fetchEmailAttachment, onFetchEmailAttachment);
  yield takeEvery(checkEmailSupporting, onCheckEmailSupporting);
  yield takeLatest(fetchEmailSyncProgress.request, onFetchEmailSyncProgress);
  yield takeLeading(makeEmailResourcePublic, onMakeEmailResourcePublic);
  // Each dispatch carries its own callback, so a cancelled task leaves that caller's promise
  // unsettled forever (takeLatest). Callers that hold the result in state must ignore a
  // response they are no longer waiting for — cancellation used to hide that for them.
  yield takeEvery(fetchSentEmailEntities, onFetchSentEmailEntities);
  yield takeLeading(fetchEmailDrafts.request, onFetchEmailDrafts);
  yield takeEvery(createEmailDraft.request, onCreateEmailDraft);
  yield takeEvery(updateEmailDraft.request, onUpdateEmailDraft);
  yield takeEvery(deleteEmailDraft.request, onDeleteEmailDraft);
  yield takeEvery(subscribeEntitiesEmails, onSubscribeEntitiesEmails);
  yield takeEvery(unsubscribeEntitiesEmails, onUnsubscribeEntitiesEmails);
  yield takeLatest(generateEmailContent.request, onGenerateEmailContent);
  yield takeEvery(submitEmailAiFeedback, onSubmitEmailAiFeedback);
}
